<template>
  <material-card
    v-bind="$attrs"
    class="v-card--material-chart"
    full-header
    :color="color"
    :loading="false"
    :progress-color="progressColor"
  >
    <template #heading>
      <div
        class="pa-4"
        style="min-height: 185px"
      >
        <v-window
          :value="usedState"
        >
          <v-window-item
            value="Deploying"
          >
            <v-img
              style="filter: drop-shadow( 3px 3px 2px rgba(0, 0, 0, .28));"
              src="@/assets/AppStarting.svg"
              max-height="150"
              contain
            />
          </v-window-item>
          <v-window-item
            value="Running"
          >
            <chartist
              v-if="data.labels.length"
              :data="data"
              :event-handlers="eventHandlers"
              :options="options"
              type="Line"
              style="max-height: 150px;"
            />
            <div
              v-else
              class="align-middle justify-center text-center pt-6"
            >
              <v-icon size="80">
                mdi-chart-timeline-variant
              </v-icon>
              <h3 class="white--text">
                No data point available
              </h3>
            </div>
          </v-window-item>
          <v-window-item value="Failed">
            <v-img
              src="@/assets/AppFailed.svg"
              max-height="150"
              contain
            />
          </v-window-item>
          <v-window-item value="Stopped">
            <v-img
              src="@/assets/AppStopped.svg"
              max-height="150"
              contain
            />
          </v-window-item>
        </v-window>
      </div>
    </template>
    <template #subtitle>
      <slot name="subtitle" />
    </template>

    <slot />
    <template #actions>      
      <build-o 
        :universe="app.universe"
        :app="app.name"
        class="mt-6 mr-2"
      />
      <app-state
        :universe="app.universe"
        :app="app.name"
        class="mt-6"
      />
      <v-btn
        v-if="usedState==='Running'"
        fab
        depressed        
        class="mt-n2 ml-2"
        :color="showCpu ? 'accent' : 'info'"
        @click="showCpu = !showCpu"
      >
        <v-icon 
          color="black"
          v-html="showCpu ? 'mdi-memory' : 'mdi-cpu-64-bit'" 
        />
      </v-btn>      
      <v-spacer />
      <v-btn
        color="primary"
        :to="`/ui/universe/${app.universe}/apps/app/${app.name}/control`"
        large
        text
        class="mt-n2"
      >
        Details
      </v-btn>
    </template>
  </material-card>
</template>

<script>
  import BuildO from '@/layouts/App/widgets/BuildO'
  import AppState from '@/layouts/App/widgets/State'
  import { mapActions } from 'pinia'
  import { useAppStore } from '@/store/app-store'
  export default {
    name: 'InfinityAppCard',

    components: {
      BuildO,
      AppState,
    },
    inheritAttrs: false,
    props: {
      app: {
        type: Object,
        default: () => ({}),
      },
      eventHandlers: {
        type: Array,
        default: () => ([]),
      },
    },
    data: () => {
      return {
        showCpu: true,
        metrics: [],
        tick: null,
        state: '',
      }
    },
    computed: {
      usedState () {
        return this.state 
      },
      data () {
        const seriesKey = this.showCpu ? 'Cpu' : 'Memory'
        const maxResource = this.showCpu ? this.app.MaxCpu || 1 : this.app.MaxMemory || 1000000000
        const data = []
        const timestamp = []

        this.metrics.forEach(metric => {
          const pct = ((100 * metric[seriesKey]) / maxResource).toFixed(3)
          data.push(pct)
          timestamp.push(metric.Timestamp)
        })

        if (this.usedState !== 'Running' || !data.length) {
          return {
            labels: [],
            series: [[]],
          }
        }
        return {
          labels: timestamp,
          series: [data],
        }
      },
      progressColor () {
        switch (this.usedState) {
          case 'Deploying':
            return 'grey'
          case 'Failed':
            return 'white'
          case 'Stopped':
            return 'grey'
        }
        return 'grey'
      },
      options () {
        return {
          axisY: {
            showGrid: false,
          },
          axisX: {
            showGrid: false,
            showLabel: false,
          },
          low: 0,
          high: 100,
          showPoint: false,
          showArea: true,
          chartPadding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
        }
      },
      color () {
        switch (this.usedState) {
          case 'Deploying':
            return 'blue-grey lighten-5'
          case 'Running':
            return this.showCpu ? 'info' : 'accent'
          case 'Failed':
            return 'error'          
          case 'Stopped':
            return 'blue-grey lighten-5'
        }
        return 'blue-grey lighten-5'
      },
    },
    beforeDestroy () {
      clearInterval(this.tick)
    },   
    unmounted () {
      clearInterval(this.tick)
    },
    beforeRouteLeave (to, from, next) {
      clearInterval(this.tick)
      next()
    },
    deactivated () {
      clearInterval(this.tick)
    },
    async mounted () {      
      const metricsData = await this.loadMetrics(this.app.universe, this.app.id)
      this.metrics = metricsData.metrics
      this.state = metricsData.state
      this.tick = setInterval(async () => {
        const metricsData = await this.loadMetrics(this.app.universe, this.app.id)
        this.metrics = metricsData.metrics
        this.state = metricsData.state        
      }, [2500, 2900, 3300, 3700, 4100][Math.floor(Math.random() * 5)])
    },
    methods: {
      ...mapActions(useAppStore, ['loadMetrics']),
    },
  }
</script>
<style lang="sass">
  .v-card--material-chart
    p
      color: #999

    .v-card--material__sheet
      max-height: 185px
      width: 100%

      .ct-label
        color: rgba(255, 255, 255, 1)
        opacity: .7
        font-size: 0.875rem
        font-weight: 300

      .ct-grid
        stroke: hsla(0,0%,100%,.2)

      .ct-series-a .ct-point,
      .ct-series-a .ct-line,
      .ct-series-a .ct-bar,
      .ct-series-a .ct-slice-donut
          stroke: rgba(255,255,255,.8)

      .ct-series-a .ct-slice-pie,
      .ct-series-a .ct-area
          fill: rgba(0,0,0,1) ! important
</style>
